import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import {
  AbstractSheetLineShape,
  SheetLineStyleSet,
} from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetLineShape';
import { SheetShapeStyleType } from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetShape';
import { SheetShapeDeepFragment } from 'src/gql/graphql';
import Edit2D = Autodesk.Extensions.Edit2D;
import GuiViewer3D = Autodesk.Viewing.GuiViewer3D;
import Viewer3D = Autodesk.Viewing.Viewer3D;

export class SplitLineShape extends AbstractSheetLineShape {
  constructor(
    viewer: Viewer3D | GuiViewer3D,
    edit2d: Edit2D,
    activeSheetData: ActiveSheetData,
    shapeModel?: SheetShapeDeepFragment,
    line?: THREE.Vector3[],
    styleSet?: SheetLineStyleSet
  ) {
    super(viewer, edit2d, activeSheetData, shapeModel);
    const convertedLine =
      shapeModel && shapeModel.sheetShapeSplitLine
        ? shapeModel.sheetShapeSplitLine.points.map((point) =>
            super.convertPointToRender(point)
          )
        : line;

    if (shapeModel && !shapeModel.sheetShapeSplitLine) {
      throw new Error('shapeModel.sheetShapeSplitLine must be defined');
    } else if (!shapeModel && !Array.isArray(line)) {
      throw new Error('One shapeModel or point must be passed');
    } else if (!convertedLine) {
      throw new Error('Cannot define point');
    }

    // Create separate line segments for each pair of points
    const { shapes, textLabelPoints } = this.createLineSegments(convertedLine);
    this.shapes = shapes;
    this.textLabelPoints = textLabelPoints;

    this.styleSet = styleSet || null;

    this.setStyle(SheetShapeStyleType.default);
  }

  private createLineSegments(convertedLine: { x: number; y: number }[]) {
    const shapes: Autodesk.Edit2D.Shape[] = [];
    const textLabelPoints: { x: number; y: number }[] = [];

    // For drain lines, we process points in pairs to create line segments
    // Each pair represents a separate drain line segment: [start1, end1, start2, end2, start3, end3, ...]
    for (let i = 0; i < convertedLine.length - 1; i += 2) {
      const startPoint = convertedLine[i];
      const endPoint = convertedLine[i + 1];

      if (startPoint && endPoint) {
        // Create a line segment from this pair of points using Polyline
        const lineSegment = new Autodesk.Edit2D.Polyline([
          startPoint,
          endPoint,
        ]);
        shapes.push(lineSegment);

        // Add a text label point at the midpoint of this segment
        const midPoint = {
          x: (startPoint.x + endPoint.x) / 2,
          y: (startPoint.y + endPoint.y) / 2,
        };
        textLabelPoints.push(midPoint);
      }
    }

    return { shapes, textLabelPoints };
  }

  public hitTest(x: number, y: number) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.shapes.some((shape) => shape.hitTest(x, y, 0.1));
  }
}
